import { useCallback, useMemo } from "react";

import { faCircleCheck, faCircleX, faList } from "@fortawesome/pro-regular-svg-icons";
import {
  faCircleCheck as faCircleCheckSolid,
  faCircleX as faCircleXSolid,
  faList as faListSolid,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import type { Status } from "api/schemas/CareEventBilling";
import { BulkActionsForm } from "shared/atoms/BulkActionsForm";
import { ButtonsGroup } from "shared/atoms/ButtonsGroup";

type TabItem = {
  value: Status;
  label: React.ReactNode;
  icon: React.ReactNode;
  iconActive: React.ReactNode;
};

type StatusCounts = Record<Status, number>;

interface TabsProps {
  filter: Status;
  statusCounts: StatusCounts;
  onTabChange: (status: Status) => void;
}

interface BulkActionsProps {
  selectedCount: number;
  onComplete: () => void;
  onDiscard: () => void;
}

export function BillingTabs({ filter, statusCounts, onTabChange }: TabsProps) {
  const { t } = useTranslation();

  const tabs = useMemo<TabItem[]>(
    () => [
      {
        value: "pending",
        icon: <FontAwesomeIcon icon={faList} />,
        iconActive: <FontAwesomeIcon icon={faListSolid} />,
        label: (
          <TabLabel>
            {t("admin_billing.buttons.pending")}
            {statusCounts.pending > 0 && <Counter $active={filter === "pending"}>{statusCounts.pending}</Counter>}
          </TabLabel>
        ),
      },
      {
        value: "completed",
        icon: <FontAwesomeIcon icon={faCircleCheck} />,
        iconActive: <FontAwesomeIcon icon={faCircleCheckSolid} />,
        label: (
          <TabLabel>
            {t("admin_billing.buttons.completed")}
            {statusCounts.completed > 0 && <Counter $active={filter === "completed"}>{statusCounts.completed}</Counter>}
          </TabLabel>
        ),
      },
      {
        value: "discarded",
        icon: <FontAwesomeIcon icon={faCircleX} />,
        iconActive: <FontAwesomeIcon icon={faCircleXSolid} />,
        label: (
          <TabLabel>
            {t("admin_billing.buttons.discarded")}
            {statusCounts.discarded > 0 && <Counter $active={filter === "discarded"}>{statusCounts.discarded}</Counter>}
          </TabLabel>
        ),
      },
    ],
    [t, statusCounts, filter]
  );

  const isStatus = (value: string): value is Status =>
    value === "pending" || value === "completed" || value === "discarded";

  const setActive = useCallback(
    (value: string) => {
      if (isStatus(value)) onTabChange(value);
    },
    [onTabChange]
  );

  return <ButtonsGroup group={tabs} active={filter} setActive={setActive} />;
}

export function BillingBulkActions({ selectedCount, onComplete, onDiscard }: BulkActionsProps) {
  const { t } = useTranslation();

  if (selectedCount <= 0) return null;

  return (
    <BulkActionsForm
      options={[
        { label: t("admin_billing.buttons.complete"), value: "complete" },
        { label: t("admin_billing.buttons.discard"), value: "discard" },
      ]}
      onSubmit={value => {
        if (value === "complete") onComplete();
        else if (value === "discard") onDiscard();
      }}
    />
  );
}

const TabLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const Counter = styled.span<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: auto;
  padding: 4px 4px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.redesign.b100};
  background: ${({ theme, $active }) => ($active ? theme.colors.white : theme.colors.redesign.b15)};
`;
